/**
 * /common-illnesses-vietnam
 *
 * Non-diagnostic traveller health guide. Describes common illnesses visitors
 * face in Vietnam (why they happen + when to seek emergency help), plus
 * insurance-document support. No diagnoses, no self-treatment / dosing
 * instructions. Insurer eligibility & coverage decisions rest with insurers.
 */
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  UrgentIntro, UrgentCardGrid, UrgentFaq, UrgentCtaBand,
  CoordinationDisclaimer, RelatedServices,
} from '../components/UrgentCareSections';
import { PUBLIC_PAGE_SEO } from '../data/publicPageSeo';
import { Stethoscope, ArrowRight, AlertTriangle, FileText, Plane, Languages } from 'lucide-react';

const SITE = 'https://vietclinics.com';
const SEO = PUBLIC_PAGE_SEO.commonIllnesses;
const HERO_IMAGE = 'https://images.pexels.com/photos/2325446/pexels-photo-2325446.jpeg?auto=compress&cs=tinysrgb&w=1400';
const HERO_FALLBACK = 'https://images.pexels.com/photos/338515/pexels-photo-338515.jpeg?auto=compress&cs=tinysrgb&w=1400';

const INTRO_PARAGRAPHS = [
  'Vietnam is a wonderful place to travel, but a change of climate, food, water and pace catches many visitors out. The good news is that most illnesses travellers experience here are common and treatable — often without leaving your accommodation. This guide is general information only; it is not a diagnosis and does not replace a consultation with a doctor.',
  'Below you will find the illnesses visitors most often ask us about, a short note on why each tends to happen, and clear warning signs that mean you should seek emergency help. If you are unwell and would rather be seen where you are, VietClinics provides 24/7 urgent, non-emergency doctor call-outs — a licensed English-speaking doctor arrives at your hotel, apartment or Airbnb within 60 minutes to examine, diagnose and treat.',
  'For life-threatening emergencies, always dial 115 for an ambulance or go directly to the nearest hospital emergency department.',
];

const ILLNESSES = [
  {
    title: "Travellers' diarrhoea & food poisoning",
    body: 'Why it happens: new bacteria in unfamiliar food and water, street food, ice and under-cooked seafood are the most common triggers for visitors. A doctor can assess how unwell you are, check for dehydration and treat the cause rather than just the symptoms.',
  },
  {
    title: 'Dehydration & heat exhaustion',
    body: 'Why it happens: heat, humidity, lots of walking and fluid lost through an upset stomach add up quickly, especially in central and southern Vietnam. In-room IV therapy and a doctor assessment can help you rehydrate and recover faster.',
  },
  {
    title: 'Fever & flu-like illness',
    body: 'Why it happens: respiratory viruses spread easily on flights, in air-conditioned spaces and in crowds. A doctor can check your temperature, chest and throat, and advise whether it is something that needs further testing.',
  },
  {
    title: 'Dengue fever (mosquito-borne) — awareness',
    body: 'Why it matters: dengue is present in Vietnam and is spread by mosquitoes, with symptoms such as high fever, headache and body aches. It should always be assessed by a doctor — do not self-treat a persistent high fever. Seek emergency care for the warning signs listed below.',
  },
  {
    title: 'Respiratory infections & sore throat',
    body: 'Why it happens: air-conditioning, city air pollution and sudden temperature changes irritate the airways. A doctor can distinguish a simple viral sore throat from something that needs antibiotics or further care.',
  },
  {
    title: 'Skin infections, cuts & insect bites',
    body: 'Why it happens: in a hot, humid climate, minor cuts, scrapes and bites can become infected quickly. A doctor can clean and dress wounds properly and check whether a bite or rash needs treatment.',
  },
  {
    title: 'Motion sickness & altitude discomfort',
    body: 'Why it happens: long bus and boat journeys and higher-altitude areas such as Sapa (Sa Pa) can leave travellers nauseous, light-headed or exhausted. A doctor can assess your symptoms and rule out anything more serious.',
  },
  {
    title: 'Travel fatigue & hangover',
    body: 'Why it happens: jet lag, late nights, heat and alcohol are a common combination on holiday. IV rehydration and a quick doctor check can get you back on your feet — and confirm nothing more serious is going on.',
  },
];

const RED_FLAGS = [
  'Difficulty breathing or shortness of breath',
  'Chest pain or pressure',
  'Severe or constant abdominal pain',
  'Repeated vomiting and being unable to keep any fluids down',
  'Blood in vomit, stool or urine',
  'Confusion, fainting or a seizure',
  'High fever with a stiff neck or a spreading rash',
  'Swelling of the face, lips or throat, or a severe allergic reaction',
  'Signs of severe dehydration (no urine, sunken eyes, extreme weakness)',
  'Any symptom that is rapidly getting worse',
];

const FAQ = [
  { q: 'Are these illnesses usually serious?', a: 'Most are common and treatable, and many can be handled at your accommodation. But some symptoms are warning signs of something more serious — if you have any of the red-flag symptoms on this page, dial 115 or go to the nearest hospital emergency department straight away.' },
  { q: 'Can a doctor come to my hotel for a common illness?', a: 'Yes. VietClinics provides 24/7 urgent, non-emergency doctor call-outs. In every listed city and service area a licensed English-speaking doctor arrives at your hotel, apartment or Airbnb within 60 minutes to examine, diagnose and treat.' },
  { q: 'Can you diagnose me over the phone or online?', a: 'A doctor can give you a video consultation for triage and guidance, but a proper diagnosis usually needs an in-person examination. This page is general information only and is not a diagnosis.' },
  { q: 'Will my travel insurance cover a doctor visit?', a: 'We can organise itemised invoices, receipts, a visit summary and relevant medical reports, prescriptions or test results from the treating provider. Whether a claim is covered is always decided by your insurer — we help you assemble the paperwork; we do not bill insurers directly or guarantee reimbursement.' },
  { q: 'Which cities do you cover?', a: 'We provide 24/7 doctor call-outs in Da Nang, Hoi An, Hanoi, Ho Chi Minh City, Nha Trang, Phu Quoc and Sapa (Sa Pa), with the same 60-minute doctor-arrival promise in every listed service area.' },
];

const RELATED = [
  { to: '/doctor-to-hotel-vietnam', icon: Stethoscope, title: 'Doctor-to-Hotel Visits', body: 'A licensed English-speaking doctor to your hotel, apartment or Airbnb within 60 minutes, 24/7.' },
  { to: '/english-speaking-doctor-vietnam', icon: Languages, title: 'English-Speaking Doctors', body: 'Hotel, apartment and Airbnb visits plus same-day online consultations — all in English.' },
  { to: '/urgent-medical-care', icon: Plane, title: '24/7 Urgent Doctor Call-Outs', body: 'See how our 24/7 urgent, non-emergency doctor call-out service works across Vietnam.' },
];

const CommonIllnessesVietnamPage = () => {
  const canonical = `${SITE}${SEO.path}`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'MedicalWebPage',
        '@id': `${canonical}#webpage`,
        url: canonical,
        name: SEO.title,
        description: SEO.description,
        inLanguage: 'en',
        about: { '@type': 'MedicalCondition', name: 'Common travel illnesses in Vietnam' },
        audience: { '@type': 'PeopleAudience', audienceType: 'Tourists, expats and international visitors in Vietnam' },
      },
      {
        '@type': 'FAQPage',
        '@id': `${canonical}#faq`,
        mainEntity: FAQ.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${canonical}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
          { '@type': 'ListItem', position: 2, name: 'Urgent Medical Care', item: `${SITE}/urgent-medical-care` },
          { '@type': 'ListItem', position: 3, name: 'Common Illnesses in Vietnam', item: canonical },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white" data-testid="common-illnesses-page">
      <Helmet>
        <title>{SEO.title}</title>
        <meta name="description" content={SEO.description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={SEO.title} />
        <meta property="og:description" content={SEO.description} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={HERO_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Header />

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-white" data-testid="illness-hero">
        <div
          aria-hidden
          className="absolute inset-0"
          style={{ background: 'linear-gradient(135deg, #F0FAF8 0%, #DCEFEC 60%, #ffffff 100%)' }}
        />
        <div className="relative container mx-auto px-4 max-w-7xl py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[11px] font-bold text-[#1A8F8A] uppercase tracking-widest mb-4 inline-block bg-white/80 backdrop-blur px-3 py-1.5 rounded-full border border-[#DCEFEC]">
                Traveller health guide
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-[1.05] mb-5">
                {SEO.h1}
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed max-w-xl mb-7">
                {SEO.intro}
              </p>
              <div className="mb-6">
                <Link
                  to="/urgent-medical-care#urgent-sos"
                  className="inline-flex items-center gap-2 text-white font-bold rounded-2xl px-7 py-4 transition-all hover:-translate-y-0.5"
                  style={{ background: 'linear-gradient(135deg, #1FA7A1 0%, #178f8a 100%)', boxShadow: '0 12px 24px rgba(31,167,161,0.25)' }}
                  data-testid="illness-hero-cta"
                >
                  <Stethoscope className="h-4 w-4" />
                  Get a 24/7 Doctor Call-Out
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="max-w-xl">
                <CoordinationDisclaimer testId="illness-disclaimer" />
              </div>
            </div>
            <div className="relative rounded-3xl overflow-hidden bg-slate-100" style={{ aspectRatio: '4 / 3' }}>
              <img
                src={HERO_IMAGE}
                onError={(e) => { e.currentTarget.src = HERO_FALLBACK; }}
                alt="Traveller resting at their accommodation in Vietnam"
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      <UrgentIntro
        heading="Common travel illnesses in Vietnam — what to know"
        paragraphs={INTRO_PARAGRAPHS}
        testId="illness-intro"
      />

      <UrgentCardGrid
        eyebrow="Common illnesses"
        heading="Illnesses travellers most often face in Vietnam"
        items={ILLNESSES}
        testId="illness-cards"
      />

      {/* ── WHEN TO SEEK EMERGENCY HELP ──────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-white" data-testid="illness-red-flags">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="bg-amber-50/70 border border-amber-100 rounded-2xl p-6 lg:p-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="h-5 w-5 text-amber-700" />
              </span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                When to seek emergency help
              </h2>
            </div>
            <p className="text-sm text-amber-900/90 leading-relaxed mb-4">
              Call <strong>115</strong> for an ambulance in Vietnam, or go directly to the nearest hospital
              emergency department, if you or someone with you has any of these warning signs:
            </p>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
              {RED_FLAGS.map((flag, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700 leading-relaxed" data-testid={`red-flag-${i + 1}`}>
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-600 flex-shrink-0" />
                  {flag}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── INSURANCE DOCUMENTS ──────────────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-[#F7FAF9]" data-testid="illness-insurance">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-11 h-11 rounded-xl bg-[#1FA7A1]/10 flex items-center justify-center flex-shrink-0">
              <FileText className="h-5 w-5 text-[#1FA7A1]" />
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              Travel insurance & documents
            </h2>
          </div>
          <p className="text-slate-600 text-base leading-relaxed mb-4">
            If you plan to claim on your travel or health insurance, it helps to keep the right paperwork from
            every consultation. After a doctor call-out, VietClinics can help organise:
          </p>
          <ul className="space-y-2 mb-5">
            {[
              'Itemised invoices and receipts for the visit and any treatment',
              'A visit summary or medical report from the treating doctor',
              'Copies of prescriptions and any test results',
              'Proof of payment for your claim file',
            ].map((doc, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700 leading-relaxed" data-testid={`insurance-doc-${i + 1}`}>
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#1FA7A1] flex-shrink-0" />
                {doc}
              </li>
            ))}
          </ul>
          <p className="text-sm text-slate-500 leading-relaxed">
            Whether a claim is covered — and how much is reimbursed — is always decided by your insurer.
            Eligibility and coverage decisions rest with your insurer; VietClinics helps you assemble the
            documents but does not bill insurers directly or guarantee reimbursement.
          </p>
        </div>
      </section>

      <UrgentFaq
        heading="Common illnesses in Vietnam — frequently asked"
        items={FAQ}
        testId="illness-faq"
      />

      <UrgentCtaBand
        heading="Feeling unwell in Vietnam?"
        subheading="Tell us your symptoms and your address — a licensed English-speaking doctor arrives within 60 minutes, 24/7."
        primaryCta={{ to: '/urgent-medical-care#urgent-sos', label: 'Get a 24/7 Doctor Call-Out' }}
        testId="illness-cta"
      />

      <RelatedServices heading="Related VietClinics services" links={RELATED} testId="illness-related" />

      <Footer />
    </div>
  );
};

export default CommonIllnessesVietnamPage;
